import React, { useState, useEffect, useRef } from 'react';
import { Card, Button } from 'react-bootstrap';
import { userStore } from '../../store/UserStore';
import { Formik, Field, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Toast from 'shared/common/toast';
import DashboardHttp from 'pages/profile/services/profile.service';
import CustomerOnboardingHttp from 'pages/customer-onboarding/services/customer.service';
import SignUpService from './newsignin.service';
import api from '../../api/client';
import { useHistory } from 'react-router-dom';
import LoginService from 'pages/login/services/login.service';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormValues {
  displayName: string;
  email: string;
  pincode: string;
  referralCode: string;
  consent: boolean;
}

// ─── Schema ───────────────────────────────────────────────────────────────────

const userSchema = Yup.object().shape({
  displayName: Yup.string().required('Display Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  pincode: Yup.string()
    .matches(/^\d{6}$/, 'Pincode must be exactly 6 digits')
    .required('Pincode is required'),
  referralCode: Yup.string(),
  consent: Yup.boolean().oneOf([true], 'You must accept the terms and conditions'),
});

// ─── Component ────────────────────────────────────────────────────────────────

const CustomerDetails = () => {
  const history = useHistory();
  const token = api.getToken();

  const [user] = userStore((state: any) => [state.user]);

  // Form meta
  const [btnLoading, setBtnLoading] = useState(false);

  // Pincode
  const [pincode, setPincode]           = useState('');
  const [pincodeError, setPincodeError] = useState('');
  const [selectedState, setSelectedState] = useState('');

  // Referral
  const [referralCode, setReferralCode]   = useState('');
  const [busPartName, setBusPartName]     = useState('');
  const [isInvalidRC, setIsInvalidRC]     = useState(false);
  const [invalidRC, setInvalidRC]         = useState('');

  // Tracks the pre-filled code so Formik gets it on first render
  const [initialReferralCode, setInitialReferralCode] = useState('');

  const formikRef = useRef<any>(null);

  // ─── Init ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    prefillReferralFromStorage();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const prefillReferralFromStorage = () => {
    try {
      const buyMethod = JSON.parse(localStorage.getItem('buyMethod') || '{}');
      const code: string = buyMethod?.ref ?? '';
      if (!code) return;

      setInitialReferralCode(code);
      setReferralCode(code);
      validateReferralCode(code);
    } catch {
      // malformed localStorage value — ignore
    }
  };

  // ─── Referral validation ───────────────────────────────────────────────────

  const validateReferralCode = async (code: string) => {
    try {
      const res: any = await LoginService.isValidReferralCode(code);
      setBusPartName(res?.data?.data[0]?.businessName ?? '');
      setIsInvalidRC(false);
      setInvalidRC('');
    } catch {
      setIsInvalidRC(true);
      setInvalidRC('Invalid Referral Code');
      setBusPartName('');
    }
  };

  const isValidReferral = async (
    code: string,
    setFieldError: FormikHelpers<FormValues>['setFieldError'],
  ) => {
    try {
      const res: any = await LoginService.isValidReferralCode(code);
      setBusPartName(res?.data?.data[0]?.businessName ?? '');
      setIsInvalidRC(false);
      setInvalidRC('');
      setFieldError('referralCode', '');
    } catch {
      setIsInvalidRC(true);
      setInvalidRC('Invalid Referral Code');
      setBusPartName('');
      setFieldError('referralCode', 'Invalid Referral Code');
    }
  };

  // ─── Pincode ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (pincode.length === 6) checkPincode(pincode);
  }, [pincode]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkPincode = async (value: string) => {
    try {
      const response: any = await CustomerOnboardingHttp.getStatebyPincode(value);
      if (response.status === 200 && response.data) {
        setSelectedState(response.data?.data?.stateName);
        setPincodeError('');
      }
    } catch {
      setPincodeError('Pincode not serviceable');
      setSelectedState('');
    }
  };

  // ─── Submit ────────────────────────────────────────────────────────────────

  const createIndCustomer = async (values: FormValues) => {
    if (isInvalidRC) return toast.error(invalidRC);
    if (pincodeError) return;

    setBtnLoading(true);
    try {
      const cusRes: any = await CustomerOnboardingHttp.createCustomer(
        { customerType: 'IND', displayName: values.displayName, kyc: {} },
        user?.userId,
      );

      if (cusRes.status !== 200 || !cusRes.data) return;

      const detailsRes = await SignUpService.createCustomerDetails(values, token);
      if (detailsRes.status !== 200) return;

      const accRes: any = await CustomerOnboardingHttp.createAccount(
        {
          customerId: cusRes.data.data.customerId,
          pinCode: pincode,
          stateName: selectedState,
        },
        user?.userId,
      );

      if (accRes.status === 200 && accRes.data) {
        toast.success('New customer account has been created.');
        history.push('/customers');
      }
    } catch (e: any) {
      toast.error(e?.data?.errors?.[0]?.title ?? 'Something went wrong');
    } finally {
      setBtnLoading(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <Toast />
      <div className='row justify-content-md-center'>
        <div className='col-md-6 col-md-offset-3 mt-3'>
          <Card>
            <Card.Body className='p-5 pt-3 pb-0'>
              <div className='text-center mb-4'>
                <Card.Title style={{ color: '#99834d' }}>
                  Create New Customer Account
                </Card.Title>
              </div>

              <Formik
                innerRef={formikRef}
                enableReinitialize={false}
                initialValues={{
                  displayName: '',
                  email: '',
                  pincode: '',
                  referralCode: initialReferralCode,
                  consent: true,
                } as FormValues}
                validationSchema={userSchema}
                onSubmit={(values, { setSubmitting }) => {
                  setSubmitting(false);
                  createIndCustomer(values);
                }}
              >
                {({ values, errors, touched, setFieldValue, setFieldError }) => {
                  // eslint-disable-next-line react-hooks/rules-of-hooks
                  useEffect(() => {
                    if (initialReferralCode) {
                      setFieldValue('referralCode', initialReferralCode);
                    }
                  }, [initialReferralCode]); // eslint-disable-line react-hooks/exhaustive-deps

                  return (
                    <Form>
                      {/* Display Name */}
                      <div className='form-group mb-3'>
                        <label className='form-label'>
                          Display Name<span style={{ color: 'red' }}> *</span>
                        </label>
                        <Field
                          type='text'
                          name='displayName'
                          className='form-control'
                          placeholder='Your Name as per PAN Card'
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value.toUpperCase();
                            if (/^[A-Za-z ]*$/.test(value)) {
                              setFieldValue('displayName', value);
                            }
                          }}
                        />
                        {errors.displayName && touched.displayName && (
                          <div className='text-danger small mt-1' style={{ fontSize: '12px' }}>
                            {errors.displayName}
                          </div>
                        )}
                      </div>

                      {/* Email */}
                      <div className='form-group mb-3'>
                        <label className='form-label'>
                          Email<span style={{ color: 'red' }}> *</span>
                        </label>
                        <Field
                          type='email'
                          name='email'
                          className='form-control'
                          placeholder='Enter your email address'
                        />
                        {errors.email && touched.email && (
                          <div className='text-danger small mt-1' style={{ fontSize: '12px' }}>
                            {errors.email}
                          </div>
                        )}
                      </div>

                      {/* Pincode & State */}
                      <div className='row mb-3'>
                        <div className='form-group col-md-6'>
                          <label className='form-label'>
                            Pincode<span style={{ color: 'red' }}> *</span>
                          </label>
                          <Field
                            type='text'
                            name='pincode'
                            className='form-control'
                            placeholder='Enter 6-digit pincode'
                            value={values.pincode}
                            maxLength={6}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const value = e.target.value.replace(/\D/g, '');
                              if (value.length <= 6) {
                                setFieldValue('pincode', value);
                                setPincode(value);
                                setPincodeError('');
                                setSelectedState(''); // clear state on every change
                              }
                            }}
                          />
                          {errors.pincode && touched.pincode && (
                            <div className='text-danger mt-1' style={{ fontSize: '12px' }}>
                              {errors.pincode}
                            </div>
                          )}
                          {pincodeError && (
                            <div className='text-danger mt-1' style={{ fontSize: '12px' }}>
                              {pincodeError}
                            </div>
                          )}
                        </div>

                        <div className='form-group col-md-6'>
                          <label className='form-label'>State</label>
                          <Field
                            type='text'
                            className='form-control'
                            disabled
                            value={selectedState}
                            placeholder='Auto-filled from pincode'
                          />
                        </div>
                      </div>

                      {/* Referral Code */}
                      <div className='form-group mb-3'>
                        <label className='form-label'>Referral Code (Optional)</label>
                        <Field
                          type='text'
                          name='referralCode'
                          className='form-control'
                          placeholder='Enter referral code'
                          value={referralCode}
                          maxLength={12}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = e.target.value;
                            setReferralCode(value);
                            setFieldValue('referralCode', value);
                            setIsInvalidRC(false);
                            setInvalidRC('');
                            setBusPartName('');
                            if (value) isValidReferral(value, setFieldError);
                          }}
                        />
                        {busPartName && (
                          <div className='text-success small mt-1'>
                            Referral from: <strong>{busPartName}</strong>
                          </div>
                        )}
                        {errors.referralCode && touched.referralCode && (
                          <div className='text-danger small mt-1'>{errors.referralCode}</div>
                        )}
                      </div>

                      {/* Consent */}
                      <div className='form-group mb-3 d-flex align-items-center'>
                        <Field
                          type='checkbox'
                          name='consent'
                          className='me-2'
                          checked={values.consent}
                        />
                        <label className='form-label mb-0'>
                          I Agree to the{' '}
                          <a
                            href='https://ebullion.in/terms-and-conditions/T&C.html'
                            target='_blank'
                            rel='noreferrer'
                            style={{ color: '#99834d' }}
                          >
                            terms and conditions
                          </a>
                        </label>
                      </div>
                      {errors.consent && touched.consent && (
                        <div className='text-danger small mb-2' style={{ fontSize: '12px' }}>
                          {errors.consent}
                        </div>
                      )}

                      {/* Submit */}
                      <div className='form-group mt-4 pb-4'>
                        <Button
                          variant='primary'
                          className='w-100'
                          size='lg'
                          type='submit'
                          disabled={btnLoading}
                        >
                          {btnLoading ? (
                            <div className='spinner-border text-white' role='status'>
                              <span className='visually-hidden'>Loading...</span>
                            </div>
                          ) : (
                            'Create Customer'
                          )}
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
